<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';

// 格式化时间函数
const formatTime = (seconds) => {
  if (!seconds || seconds === 0) return '0s';
  
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    if (minutes === 0 && remainingSeconds === 0) {
      return `${hours}h`;
    } else if (remainingSeconds === 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    }
  }
};

const { departmentTotalCount, departmentMetrics, isLoading, pageIndex } = defineProps({
  departmentTotalCount: {
    type: Number,
    default: 0,
  },
  departmentMetrics: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  pageIndex: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(['pageChange']);

const tableData = computed(() => {
  return departmentMetrics
    .map(item => {
      const { metric = {} } = item;
      return {
        departmentName: item.name || '---',
        handledChats: metric.resolutionCount || 0,
        onHoldChats: metric.onHoldCount || 0,
        queuedChats: metric.queuingCount || 0,
        agentsAccepted: metric.onlineAgentCount || 0,
        totalAgents: metric.agentTotalCount || 0,
        firstResponseTime: formatTime(metric.firstResponseTime),
        resolutionTime: formatTime(metric.avgResolutionTime),
        queueTimeAvg: formatTime(metric.avgQueuingTime),
      };
    });
});

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? 'whitespace-normal break-words' : 'text-slate-300 dark:text-slate-700',
    },
    cellProps.getValue() ? cellProps.getValue() : '---'
  );

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('departmentName', {
    header: 'Department Name',
    cell: defaulSpanRender,
    size: 200,
  }),

  columnHelper.accessor('handledChats', {
    header: 'Handled Chats',
    cell: defaulSpanRender,
    size: 140,
  }),

  columnHelper.accessor('onHoldChats', {
    header: 'On Hold Chats',
    cell: defaulSpanRender,
    size: 140,
  }),
  
  columnHelper.accessor('queuedChats', {
    header: 'Queued Chats',
    cell: defaulSpanRender,
    size: 140,
  }),
  
  columnHelper.accessor('agentsAccepted', {
    header: 'Online Agents',
    cell: defaulSpanRender,
    size: 160,
  }),
  
  columnHelper.accessor('totalAgents', {
    header: 'Total Agents',
    cell: defaulSpanRender,
    size: 140,
  }),
  
  columnHelper.accessor('firstResponseTime', {
    header: 'First Response Time',
    cell: defaulSpanRender,
    size: 180,
  }),
  
  columnHelper.accessor('resolutionTime', {
    header: 'Resolution Time',
    cell: defaulSpanRender,
    size: 160,
  }),
  
  columnHelper.accessor('queueTimeAvg', {
    header: 'Queue Time (Avg)',
    cell: defaulSpanRender,
    size: 160,
  }),
];

const paginationParams = computed(() => {
  return {
    pageIndex: pageIndex,
    pageSize: 25,
  };
});

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return departmentTotalCount;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagintaion = updater(paginationParams.value);
    emit('pageChange', newPagintaion.pageIndex);
  },
});
</script>

<template>
  <div class="department-table-container">
    <div class="overflow-x-auto shadow-[inset_-8px_0_8px_-4px_#ddd]">
      <Table :table="table" fixed class="max-h-[calc(100vh-21.875rem)]" />
    </div>
    <Pagination class="mt-2" :table="table" />
    <div v-if="isLoading" class="departments-loader">
      <Spinner />
      <span>Loading department data...</span>
    </div>
    <EmptyState
      v-else-if="!isLoading && !departmentMetrics.length"
      title="No department data available"
    />
  </div>
</template>

<style lang="scss" scoped>
.department-table-container {
  @apply flex flex-col flex-1 w-full;

  .ve-table {
    &::v-deep {
      th.ve-table-header-th {
        @apply text-sm rounded-xl;
        padding: var(--space-small) var(--space-two) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-two) !important;
      }
    }
  }

  &::v-deep .ve-pagination {
    @apply bg-transparent dark:bg-transparent;
  }

  &::v-deep .ve-pagination-select {
    @apply hidden;
  }

  .table-pagination {
    @apply mt-4 text-right;
  }
}

.departments-loader {
  @apply items-center flex text-base justify-center p-8;
}
</style>
